import { useCallback, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

/**
 * 地址异常上报灰度开关（2.2 需求点1）。
 *
 *   abnormalReportEnabled(hub) = wholeSwitch || hubIds.contains(hub)
 *
 * 单一源：后端 pickup common_config `pickup.address_abnormal_report.gray_switch`，
 * 经 api-server GraphQL `getAddressAbnormalReportGraySwitch` 暴露。
 *
 * <h3>为什么必须单独读，而不是复用 usePickupSiteV2Gray</h3>
 * 这是**两个独立开关**：2.0 灰度（`express2_gray_switch`）已开的 hub 不代表本功能已开
 * —— 本功能默认全关，就是为了能单独止血而不牵动整个 2.0。用 2.0 那个判显隐，会在
 * 「2.0 已开、异常上报未开」的 hub 上出现"按钮能点、点了返回 FEATURE_DISABLED"。
 *
 * <h3>加载中按未命中</h3>
 * 与 usePickupSiteV2Gray 同口径（`?? false`）：宁可入口晚出现一瞬，也不要先显示再报错。
 * 入口本身是次要操作，闪一下不影响主流程；反之运营点了报错会以为功能坏了。
 *
 * <p>调整灰度改后端 common_config 即可（JSON {wholeSwitch,hubIds}，缓存 60s 生效），无需发版。
 */
export const GET_ADDRESS_ABNORMAL_GRAY_SWITCH = gql`
  query GetAddressAbnormalReportGraySwitch {
    getAddressAbnormalReportGraySwitch {
      wholeSwitch
      hubIds
    }
  }
`;

interface AddressAbnormalGrayResponse {
  getAddressAbnormalReportGraySwitch?: {
    wholeSwitch: boolean;
    // 后端 hubIds 为 Long，GraphQL 序列化为字符串
    hubIds: Array<string | number> | null;
  } | null;
}

export function useAddressAbnormalGray() {
  const { data, loading } = useQuery<AddressAbnormalGrayResponse>(GET_ADDRESS_ABNORMAL_GRAY_SWITCH, {
    fetchPolicy: 'cache-first',
  });
  const cfg = data?.getAddressAbnormalReportGraySwitch;
  const wholeSwitch = cfg?.wholeSwitch ?? false;
  const hubIds = useMemo(() => (cfg?.hubIds ?? []).map(String), [cfg]);

  const abnormalReportEnabled = useCallback(
    (hubId?: string | number | null): boolean => {
      if (hubId === null || hubId === undefined || hubId === '') return false;
      return wholeSwitch || hubIds.includes(String(hubId));
    },
    [wholeSwitch, hubIds],
  );

  return { wholeSwitch, hubIds, abnormalReportEnabled, loading };
}
